import { KeyRound, PauseCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ServiceUsage } from "@/lib/api";
import { formatDateTime, formatRelativeTime } from "@/lib/format";
import { budgetOf, formatAmount, SERVICE_LABEL, SUSPENSION_SCOPE } from "@/lib/services-gateway";
import { cn } from "@/lib/utils";

/**
 * One service: this month's use against its budget, its tier and its live keys
 * -- and, while an admin has suspended it, why (owners get no resume button).
 */
export function ServiceRow({
  usage,
  isAdmin,
  canAsk,
  offGateway,
  onAsk,
  onManage,
  onSuspend,
}: {
  usage: ServiceUsage;
  isAdmin: boolean;
  /** Budget requests are open on this instance. */
  canAsk: boolean;
  /**
   * The app calls this service without the gateway (models direct, its own
   * Langfuse): no budget to ask for, and nothing to suspend.
   */
  offGateway: boolean;
  onAsk: () => void;
  onManage: () => void;
  /** Admin only: suspend this service (`true`) or resume its own suspension (`false`). */
  onSuspend: (suspend: boolean) => void;
}) {
  const budget = budgetOf(usage);
  const used = (usage.unit === "usd" ? usage.month_usd : usage.month_requests) ?? 0;
  const activeKeys = (usage.keys ?? []).filter((key) => key.status === "active");
  const scope = usage.suspended ? (usage.suspended_scope ?? null) : null;

  return (
    <div className="space-y-1.5 py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm">{SERVICE_LABEL[usage.service]}</p>
          <Badge variant="outline" className="text-xs">
            {usage.tier ?? "default tier"}
          </Badge>
          {usage.suspended && (
            <Badge variant="destructive" className="text-xs">
              <PauseCircle className="size-3" /> Suspended
            </Badge>
          )}
        </div>
        <div className="flex gap-1">
          {isAdmin && (usage.suspended ? scope === "bucket" : !offGateway) && (
            <Button size="sm" variant="ghost" onClick={() => onSuspend(!usage.suspended)}>
              {usage.suspended ? "Resume" : "Suspend"}
            </Button>
          )}
          {canAsk && !offGateway && (
            <Button size="sm" variant="ghost" onClick={onAsk}>
              Request more
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={onManage}>
              Manage
            </Button>
          )}
        </div>
      </div>

      {/* A whole-project suspension is explained once, above the services. */}
      {usage.suspended && scope !== "project" && (
        <p className="text-destructive text-xs">
          Suspended by an admin
          {usage.suspended_reason && (
            <>
              : <bdi>{usage.suspended_reason}</bdi>
            </>
          )}
          {isAdmin && scope && ` (${SUSPENSION_SCOPE[scope]})`}
          {offGateway && " — not in effect: the app calls it without the gateway"}
        </p>
      )}
      <p className="text-muted-foreground text-xs">
        {formatAmount(usage.unit, used)} of{" "}
        {budget === null ? "the environment default" : formatAmount(usage.unit, budget)} this month
      </p>
      {budget !== null && budget > 0 && (
        <progress
          value={Math.min(used, budget)}
          max={budget}
          aria-label={`${SERVICE_LABEL[usage.service]}: share of the monthly budget used`}
          className={cn(
            "bg-muted h-1.5 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-transparent",
            used >= budget
              ? "[&::-moz-progress-bar]:bg-destructive [&::-webkit-progress-value]:bg-destructive"
              : "[&::-moz-progress-bar]:bg-primary [&::-webkit-progress-value]:bg-primary",
          )}
        />
      )}
      {usage.month_unpriced && (
        <p className="text-destructive text-xs">
          Some model use has no price yet, so further calls are refused until the model is priced.
        </p>
      )}

      {activeKeys.map((key) => (
        <p
          key={key.key_id}
          className="text-muted-foreground flex items-center gap-1.5 text-xs"
          title={key.created_at ? `Created ${formatDateTime(key.created_at)}` : undefined}
        >
          <KeyRound className="size-3" />
          <code className="bg-muted/40 rounded px-1 font-mono">{key.display_prefix}…</code>
          {key.not_after
            ? `stops ${formatRelativeTime(key.not_after)}`
            : key.last_used_at
              ? `last used ${formatRelativeTime(key.last_used_at)}`
              : "not used yet"}
        </p>
      ))}
    </div>
  );
}
